import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { faqPageSchema, type Faq } from "@/data/serviceFaqs";
import apexTheater from "@/assets/Apex_Theater.webp.asset.json";

const hero = apexTheater.url;
const URL = "https://apexav.com/home-theater-installation-near-me";

/** Nearby neighborhoods and suburbs, each with copy written for that area only. */
const NEIGHBORHOODS: { name: string; blurb: string; to: string; params?: Record<string, string> }[] = [
  {
    name: "Cherry Creek",
    blurb:
      "Brick-and-block party walls and tight alley-load garages mean cable paths are rarely obvious. We core through structure where needed and keep low-voltage off shared walls so the theater stays quiet for the neighbors.",
    to: "/cherry-creek/home-theater-installation",
  },
  {
    name: "Highlands Ranch",
    blurb:
      "Almost every request here is a basement build-out. Nine-foot ceilings with open-web floor trusses give us room for in-ceiling Atmos and a soffit-hidden projector without dropping the seating platform too far.",
    to: "/highlands-ranch/home-theater-installation",
  },
  {
    name: "Castle Rock",
    blurb:
      "Walk-out lower levels with big south-facing glass need light control before speakers. We pair blackout shading with an ambient-light-rejecting screen so the room works at 2pm, not just after dark.",
    to: "/castle-rock/home-theater-installation",
  },
  {
    name: "Castle Pines",
    blurb:
      "Reference builds on larger lots: isolated wall assemblies, dedicated circuits from the panel, tiered riser seating, and a front stage calibrated on-site once the room finishes settling.",
    to: "/castle-pines/home-theater-installation",
  },
  {
    name: "Centennial",
    blurb:
      "Late-90s and 2000s two-stories with unfinished basements. We scope structure first, then design the layout around what the floor trusses actually allow.",
    to: "/service-areas/$slug",
    params: { slug: "centennial" },
  },

  {
    name: "Littleton",
    blurb:
      "Older ranches and split-levels where the media room shares a wall with a bedroom. Decoupled channels and dense insulation keep late-night movies from carrying down the hall.",
    to: "/service-areas/$slug",
    params: { slug: "littleton" },
  },
  {
    name: "Parker",
    blurb:
      "Newer construction, which means prewire is often still possible. Catching a Parker home at framing is the cheapest time to run conduit for a future projector.",
    to: "/service-areas/$slug",
    params: { slug: "parker" },
  },
  {
    name: "Centennial",
    blurb:
      "Two-story great rooms that double as media spaces. We tune for the volume of the room rather than fighting it, and hide the subwoofer where the room's own modes help it.",
    to: "/service-areas/$slug",
    params: { slug: "centennial" },
  },
];

const faqs: Faq[] = [
  {
    q: "Who does home theater installation near me in the Denver area?",
    a: "Apex AV installs home theaters throughout the Denver metro and Front Range from our shop at 2921 W 38th Ave in Denver. We regularly work in Cherry Creek, Hilltop, Greenwood Village, Cherry Hills Village, Highlands Ranch, Lone Tree, Castle Pines, Castle Rock, Parker, Centennial, Littleton, Arvada, Golden, and Boulder. The crew that quotes your room is the crew that installs it.",
  },
  {
    q: "How much does home theater installation cost near Denver?",
    a: "A media room with a large display, in-wall or in-ceiling immersive audio, and simple one-button control typically runs $25,000 to $60,000 installed. A dedicated theater with laser projection, a motorized screen, tiered seating, acoustic treatment, and full immersive audio starts around $60,000. Every quote is fixed-bid after a free in-home walkthrough.",
  },
  {
    q: "How soon can someone come out to my house?",
    a: "Most Denver-metro consultations are booked within three to five business days. The visit takes 60 to 90 minutes: we measure the room, check ceiling cavities and panel capacity, review your existing equipment, and follow up with a written design and price within about a week.",
  },
  {
    q: "Can you install a home theater in an existing house, or only new construction?",
    a: "Retrofit is the majority of our work. We fish speaker and video cable through finished walls and ceilings, add in-ceiling channels, pull network drops, and patch and texture so the finish matches. If a wall genuinely can't be opened, we redesign around it rather than leaving exposed raceway.",
  },
  {
    q: "How long does a home theater installation take?",
    a: "A media-room upgrade in an existing finished space is usually two to five days on site. A dedicated theater built into a basement or remodel runs two to six weeks depending on framing, drywall, and equipment lead times. You get a dated schedule before we start.",
  },
  {
    q: "Do you handle the projector, screen, seating, and acoustics, or just the electronics?",
    a: "All of it. We design the seating layout and sightlines, specify screen size and gain for the room's light, treat first-reflection points and the rear wall, run power and network, mount and align the projector, and calibrate audio and video at the seated positions.",
  },
  {
    q: "Do you come back after the install?",
    a: "Yes. We return for post-install tuning as the room settles and new content exposes anything worth adjusting, and we support the system long-term. Being local is the whole point — there is no ticket queue in another state.",
  },
];

export const Route = createFileRoute("/home-theater-installation-near-me")({
  head: () => ({
    meta: [
      { title: "Home Theater Installation Near Me | Denver & Front Range — Apex AV" },
      {
        name: "description",
        content:
          "Looking for home theater installation near you? Apex AV designs, installs, and calibrates home theaters across Denver, Cherry Creek, Highlands Ranch, Castle Rock, Parker and the Front Range. Free in-home consultation.",
      },
      {
        name: "keywords",
        content:
          "home theater installation near me, home theater installers near me, home theater installation Denver, media room installation near me, home theater company near me",
      },
      { property: "og:title", content: "Home Theater Installation Near Me — Apex AV Denver" },
      {
        property: "og:description",
        content:
          "Local Denver-area home theater installation: dedicated theaters, media rooms, immersive audio, and on-site calibration.",
      },
      { property: "og:image", content: `https://apexav.com${apexTheater.url}` },
      { property: "og:image:alt", content: "Home theater installation near me — Apex AV Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Home Theater Installation Near Me — Apex AV Denver" },
      { name: "twitter:description", content: "Denver-area home theater design, installation, and calibration." },
      { name: "twitter:image", content: `https://apexav.com${apexTheater.url}` },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            faqPageSchema(faqs, URL),
            {
              "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
              "@id": `${URL}#localbusiness`,
              name: "Apex AV — Home Theater Installation Near Me",
              image: `https://apexav.com${apexTheater.url}`,
              logo: "https://apexav.com/logo.png",
              url: URL,
              telephone: "+1-303-317-3233",
              email: "info@apexav.com",
              priceRange: "$$$",
              address: {
                "@type": "PostalAddress",
                streetAddress: "2921 W 38th AVE STE 169",
                addressLocality: "Denver",
                addressRegion: "CO",
                postalCode: "80211",
                addressCountry: "US",
              },
              geo: { "@type": "GeoCoordinates", latitude: 39.7714, longitude: -105.0234 },
              areaServed: NEIGHBORHOODS.map((n) => ({ "@type": "City", name: `${n.name}, CO` })),
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  opens: "08:00",
                  closes: "18:00",
                },
                { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "16:00" },
              ],
              parentOrganization: { "@id": "https://apexav.com/#organization" },
            },
            {
              "@type": "Service",
              name: "Home Theater Installation",
              description:
                "Home theater and media room design, installation, and calibration for homes across the Denver metro and Front Range.",
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
              serviceType: "Home Theater Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "25000" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://apexav.com/" },
                { "@type": "ListItem", position: 2, name: "Home Theater Installation Near Me", item: URL },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: HomeTheaterInstallationNearMe,
});

function HomeTheaterInstallationNearMe() {
  return (
    <>
      <ServiceDetail
        eyebrow="Home theater installation near me"
        title="Home theater installation near you, across Denver and the Front Range."
        intro={
          <>
            If you searched <strong>“home theater installation near me”</strong> from a Denver-area address, you're in
            our service radius. Apex AV designs, builds, and calibrates dedicated theaters and media rooms from
            Cherry Creek to Castle Pines — measured in person, quoted fixed-bid, and supported by the same local team
            afterward.
          </>
        }
        image={hero}
        imageAlt="Home theater installation near me in Denver, Colorado — completed Apex AV theater room"
        video="/videos/home-theater-video.mp4"
        startingPrice="$25,000"
        priceNote="Media rooms from $25K. Dedicated theaters typically $60K and up."
        bullets={[
          "Free in-home measurement and design consultation",
          "Fixed-bid pricing before anyone touches a wall",
          "Dedicated theaters, basement builds, and multi-purpose media rooms",
          "Immersive surround layouts designed around your ceiling framing",
          "Laser projection, motorized screens, and ambient-light-rejecting screens",
          "Retrofit cable fishing with patch, texture, and paint",
          "On-site audio and video calibration at the seated positions",
          "Local follow-up visits, not a remote support queue",
        ]}
        features={[
          { title: "Booked in days", desc: "Denver-metro consultations are usually scheduled within five business days." },
          { title: "Measured, not guessed", desc: "We scope ceiling cavities, structure, and panel capacity before quoting." },
          { title: "Calibrated on site", desc: "Every room is tuned in person, then revisited once it settles." },
        ]}
        faqs={faqs}
        relatedServices={[
          { to: "/services/home-theater", label: "Home Theater Design & Install", desc: "The full service page: process, gear, and what a build includes." },
          { to: "/home-theater-near-me", label: "Home Theater Near Me", desc: "Local coverage, response times, and finished-theater tours." },
          { to: "/services/hidden-audio", label: "Hidden & Invisible Audio", desc: "Speakers that disappear into the wall or ceiling." },
        ]}
      />

      <section className="border-t border-border bg-card/30">
        <div className="container mx-auto px-6 py-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Nearby neighborhoods we install in</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Construction differs street to street around Denver — party walls in Cherry Creek, open-web trusses in
            Highlands Ranch, walk-out glass in Castle Rock. Here's what changes the design in each area.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {NEIGHBORHOODS.map((n) => (
              <Link
                key={n.name}
                to={n.to}
                params={n.params as never}
                className="card-elegant sheen group rounded-xl border border-border bg-card p-5 shadow-card transition hover:border-brand/50 hover:shadow-glow"
              >
                <h3 className="font-display text-lg font-semibold group-hover:text-brand-text">{n.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{n.blurb}</p>
                <span className="mt-3 inline-block text-sm text-brand-text">View area →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-16 border-t border-border">
        <h2 className="font-display text-2xl md:text-3xl font-bold">Before you build: read the design guide</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Room ratios, screen size and sightlines, speaker layout, and acoustic treatment — our{" "}
          <Link
            to="/blog/custom-home-theater-design-guide"
            className="font-semibold text-brand-text underline underline-offset-4"
          >
            custom home theater design guide
          </Link>{" "}
          walks through every decision we make on a Denver theater build, in the order it matters.
        </p>
        <Link
          to="/blog/custom-home-theater-design-guide"
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-brand/50 transition"
        >
          Read the custom home theater design guide →
        </Link>
      </section>
    </>
  );
}
